import React, { useState, useRef, useEffect, useCallback } from 'react';
import './LazyImage.css';

/**
 * LazyImage - A reusable image component with lazy loading and skeleton loading state
 *
 * Features:
 * - Native lazy loading with IntersectionObserver fallback
 * - Skeleton shimmer animation while loading
 * - Smooth fade-in transition when loaded
 * - Error handling with fallback
 * - Supports custom placeholder
 * - Optional `priority` prop for above-the-fold LCP candidates: skips the
 *   IntersectionObserver entirely, sets fetchpriority="high" and
 *   loading="eager" so the browser treats the image as render-critical.
 *
 * @param {Object} props
 * @param {string} props.src - Image source URL
 * @param {string} props.alt - Alt text for the image
 * @param {string} props.className - Additional CSS class for the image
 * @param {string} props.wrapperClassName - Additional CSS class for the wrapper
 * @param {string} props.fallbackSrc - Fallback image URL if main image fails
 * @param {string} props.loading - Loading strategy: 'lazy' (default) or 'eager'.
 *   Implicitly 'eager' when `priority` is true.
 * @param {boolean} props.priority - Mark as above-the-fold / LCP candidate.
 *   Implies loading="eager" + fetchpriority="high" and skips the observer.
 * @param {number|string} props.width - Intrinsic width (px). Forwarded to <img>
 *   so the browser can reserve space before the image loads.
 * @param {number|string} props.height - Intrinsic height (px). Same reason.
 * @param {Function} props.onLoad - Callback when image loads
 * @param {Function} props.onError - Callback when image fails to load
 * @param {Object} props.style - Additional inline styles for the image
 * @param {Object} props.wrapperStyle - Additional inline styles for the wrapper
 */
const LazyImage = ({
  src,
  alt = '',
  className = '',
  wrapperClassName = '',
  fallbackSrc = null,
  // React node rendered INSTEAD of the image when it fails to load (e.g. an
  // initials avatar). Without this the prop leaked onto the <img> via
  // ...rest and a failed load left the browser's broken-image icon behind.
  fallback = null,
  loading = 'lazy',
  priority = false,
  width,
  height,
  onLoad,
  onError,
  style = {},
  wrapperStyle = {},
  ...rest
}) => {
  // Priority images skip the IntersectionObserver and load immediately; they
  // also bypass the loading=lazy hint so the browser prioritises them.
  const effectiveLoading = priority ? 'eager' : loading;
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(effectiveLoading === 'eager');
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  // Reset state when src changes
  useEffect(() => {
    setIsLoaded(false);
    setHasError(false);
  }, [src]);

  // Check if image is already cached (for eager loading or cached images)
  useEffect(() => {
    if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      setIsLoaded(true);
    }
  }, [src, isInView]);

  // Set up IntersectionObserver for lazy loading
  useEffect(() => {
    if (effectiveLoading === 'eager' || !imgRef.current) {
      setIsInView(true);
      return;
    }

    // Use IntersectionObserver for better lazy loading control
    const options = {
      root: null,
      rootMargin: '100px', // Start loading 100px before entering viewport
      threshold: 0.01
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          // Stop observing once in view
          if (observerRef.current) {
            observerRef.current.disconnect();
          }
        }
      });
    }, options);

    observerRef.current.observe(imgRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [effectiveLoading]);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    if (onLoad) onLoad();
  }, [onLoad]);

  const handleError = useCallback(() => {
    setHasError(true);
    if (onError) onError();
  }, [onError]);

  // Image failed and the caller supplied a replacement node (initials
  // avatar etc.) — render that instead of a broken-image icon. fallbackSrc
  // (a URL retry) takes precedence when both are given.
  if (hasError && !fallbackSrc && fallback) {
    return fallback;
  }

  // Determine the actual src to use
  const imageSrc = hasError && fallbackSrc ? fallbackSrc : src;

  // Compose the dimension + priority hints we forward to the underlying <img>.
  // Width/height let the browser reserve aspect-ratio space pre-load (cuts CLS).
  // fetchpriority=high tells Chrome/WebKit to treat this as render-critical.
  const imgAttrs = {};
  if (width != null) imgAttrs.width = width;
  if (height != null) imgAttrs.height = height;
  if (priority) imgAttrs.fetchpriority = 'high';

  return (
    <div
      className={`lazy-image-wrapper ${isLoaded ? 'loaded' : 'loading'} ${wrapperClassName}`}
      style={wrapperStyle}
    >
      <img
        ref={imgRef}
        src={isInView ? imageSrc : undefined}
        data-src={src}
        alt={alt}
        className={`lazy-image ${className} ${isLoaded ? 'lazy-image-loaded' : 'lazy-image-loading'}`}
        loading={effectiveLoading}
        decoding="async"
        onLoad={handleLoad}
        onError={handleError}
        style={style}
        {...imgAttrs}
        {...rest}
      />
    </div>
  );
};

export default LazyImage;
